import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Search, Users, SlidersHorizontal, Target, Bookmark, Send, Star, X, BellPlus, Trash2, Globe2, Lock, Download } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import CandidateCard from "@/components/CandidateCard";
import CandidateDrawer from "@/components/CandidateDrawer";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAgency } from "@/lib/roles";
import { exportCandidatesHtml } from "@/lib/exportHtml";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

export default function Talent() {
  const { user } = useAuth();
  const agency = isAgency(user?.role);
  const [q, setQ] = useState("");
  const [location, setLocation] = useState("");
  const [skills, setSkills] = useState("");
  const [country, setCountry] = useState("all");
  const [caps, setCaps] = useState(null);
  const [jobId, setJobId] = useState("none");
  const [jobs, setJobs] = useState([]);
  const [clients, setClients] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [picked, setPicked] = useState({}); // id -> true
  const [searches, setSearches] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [searchName, setSearchName] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkClient, setBulkClient] = useState("");
  const [bulkNote, setBulkNote] = useState("");

  const pickedIds = Object.keys(picked).filter((k) => picked[k]);

  const loadSearches = useCallback(() => { api.get("/saved-searches").then((r) => setSearches(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    api.get("/employer/jobs").then((r) => setJobs(r.data)).catch(() => {});
    if (agency) api.get("/agency/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/billing/capabilities").then((r) => setCaps(r.data)).catch(() => {});
    loadSearches();
  }, [agency, loadSearches]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (location) params.location = location;
      if (skills) params.skills = skills;
      if (country && country !== "all") params.country = country;
      if (jobId && jobId !== "none") params.job_id = jobId;
      const { data } = await api.get("/candidates", { params });
      setCandidates(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [q, location, skills, country, jobId]);

  useEffect(() => { load(); }, [jobId]); // eslint-disable-line

  const toggleSave = async (c) => {
    try {
      const { data } = await api.post(`/candidates/${c.id}/save`);
      setCandidates((prev) => prev.map((x) => (x.id === c.id ? { ...x, saved: data.saved } : x)));
      setSelected((s) => (s && s.id === c.id ? { ...s, saved: data.saved } : s));
      toast.success(data.saved ? "Saved to talent pool" : "Removed from talent pool");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleSelect = (c) => setPicked((p) => ({ ...p, [c.id]: !p[c.id] }));
  const clearSelection = () => setPicked({});

  const bulkSave = async () => {
    try {
      const { data } = await api.post("/candidates/bulk-save", { candidate_ids: pickedIds });
      setCandidates((prev) => prev.map((x) => (pickedIds.includes(x.id) ? { ...x, saved: true } : x)));
      toast.success(`Saved ${data.saved} candidate${data.saved !== 1 ? "s" : ""} to your pool`);
      clearSelection();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const bulkSubmit = async () => {
    if (!bulkClient) { toast.error("Choose a client"); return; }
    try {
      const { data } = await api.post("/agency/submissions/bulk", { candidate_ids: pickedIds, client_id: bulkClient, note: bulkNote });
      toast.success(`Submitted ${data.submitted} candidate${data.submitted !== 1 ? "s" : ""} to client`);
      setBulkOpen(false); setBulkClient(""); setBulkNote(""); clearSelection();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveSearch = async () => {
    if (!searchName.trim()) { toast.error("Name your search"); return; }
    try {
      await api.post("/saved-searches", { name: searchName, q, location, skills, job_id: jobId === "none" ? "" : jobId });
      toast.success("Search saved — you'll be alerted on new matches");
      setSaveOpen(false); setSearchName(""); loadSearches();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const runSearch = async (s) => {
    setQ(s.q || ""); setLocation(s.location || ""); setSkills(s.skills || ""); setJobId(s.job_id || "none");
    await api.post(`/saved-searches/${s.id}/seen`).catch(() => {});
    setTimeout(load, 0); loadSearches();
  };
  const deleteSearch = async (id) => { await api.delete(`/saved-searches/${id}`).catch(() => {}); loadSearches(); };

  return (
    <div className="min-h-screen wo-bg">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8 pb-28 lg:pb-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center text-teal-400"><Users size={20} /></div>
          <div>
            <h1 className="font-display font-bold text-3xl text-slate-100">Talent Search</h1>
            <p className="text-slate-400 text-sm"><span className="font-mono text-teal-400" data-testid="talent-count">{candidates.length}</span> candidates in the database</p>
          </div>
        </div>

        {/* saved searches */}
        {searches.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="saved-searches-bar">
            <span className="text-xs text-slate-500">Saved searches:</span>
            {searches.map((s) => (
              <span key={s.id} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-slate-900/60 pl-3 pr-1.5 py-1 text-xs text-slate-200">
                <button onClick={() => runSearch(s)} data-testid="run-saved-search" className="hover:text-teal-300">{s.name}</button>
                {s.new_matches > 0 && <span data-testid="saved-search-new" className="bg-teal-400 text-slate-900 rounded-full px-1.5 font-mono font-bold">+{s.new_matches}</span>}
                <button onClick={() => deleteSearch(s.id)} data-testid="delete-saved-search" className="text-slate-500 hover:text-red-400"><X size={13} /></button>
              </span>
            ))}
          </div>
        )}

        {/* filters */}
        <div className="liquid-glass rounded-2xl p-5 mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
          <div>
            <label className="text-xs text-slate-400 flex items-center gap-1.5 mb-1.5"><Search size={13} /> Keyword</label>
            <Input data-testid="talent-search-q" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="name, title, skill…" className="bg-slate-950/50 border-white/10 text-slate-100" />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">Location</label>
            <Input data-testid="talent-search-location" value={location} onChange={(e) => setLocation(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="e.g. Berlin, Remote" className="bg-slate-950/50 border-white/10 text-slate-100" />
          </div>
          <div>
            <label className="text-xs text-slate-400 flex items-center gap-1.5 mb-1.5">
              <Globe2 size={13} /> Country
              {caps && !caps.cross_country && <Lock size={11} className="text-amber-400" />}
            </label>
            {caps && !caps.cross_country ? (
              <Link to="/upgrade" data-testid="country-locked" className="flex items-center justify-between rounded-md bg-slate-950/50 border border-amber-500/30 text-amber-300 text-sm px-3 py-2 hover:bg-amber-500/10">
                <span className="truncate">{caps.home_country || "Local only"}</span> <span className="text-[10px] font-mono">Upgrade</span>
              </Link>
            ) : (
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger data-testid="talent-country" className="bg-slate-950/50 border-white/10 text-slate-200"><SelectValue placeholder="All countries" /></SelectTrigger>
                <SelectContent className="text-slate-200 max-h-64">
                  <SelectItem value="all">All EU markets</SelectItem>
                  {(caps?.countries || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">Skills (comma)</label>
            <Input data-testid="talent-search-skills" value={skills} onChange={(e) => setSkills(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="React, Go" className="bg-slate-950/50 border-white/10 text-slate-100" />
          </div>
          <div>
            <label className="text-xs text-slate-400 flex items-center gap-1.5 mb-1.5"><Target size={13} /> Rank by match to</label>
            <Select value={jobId} onValueChange={setJobId}>
              <SelectTrigger data-testid="talent-match-job" className="bg-slate-950/50 border-white/10 text-slate-200"><SelectValue placeholder="No job" /></SelectTrigger>
              <SelectContent className="text-slate-200 max-h-64">
                <SelectItem value="none">No job (recent first)</SelectItem>
                {jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2 lg:col-span-5 flex flex-wrap gap-2 items-center">
            <Button onClick={load} data-testid="talent-search-btn" className="bg-gradient-to-r from-teal-400 to-cyan-500 text-slate-900 font-semibold hover:opacity-90">
              <SlidersHorizontal size={15} className="mr-2" /> Apply filters
            </Button>
            <Button onClick={() => setSaveOpen(true)} data-testid="save-search-btn" variant="outline" className="border-white/10 text-slate-200 hover:bg-white/5">
              <BellPlus size={15} className="mr-2" /> Save this search
            </Button>
            <Button onClick={() => exportCandidatesHtml(candidates, "Candidate Shortlist")} disabled={!candidates.length} data-testid="talent-export-btn" variant="outline" className="border-white/10 text-slate-200 hover:bg-white/5">
              <Download size={15} className="mr-2" /> Export HTML
            </Button>
            {caps && !caps.cross_country && (
              <Link to="/upgrade" className="text-xs text-amber-300 hover:underline inline-flex items-center gap-1.5"><Globe2 size={13} /> Upgrade to search candidates across Europe</Link>
            )}
          </div>
        </div>

        {loading ? (
          <p className="text-slate-500 text-center py-20">Searching…</p>
        ) : candidates.length === 0 ? (
          <div className="text-center py-24 text-slate-500" data-testid="talent-empty">
            <Users size={40} className="mx-auto text-slate-600 mb-4" /> No candidates match your filters.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {candidates.map((c) => (
              <CandidateCard key={c.id} candidate={c} onView={setSelected} onToggleSave={toggleSave}
                selectable selected={!!picked[c.id]} onToggleSelect={toggleSelect} />
            ))}
          </div>
        )}
      </div>

      {/* bulk action bar */}
      {pickedIds.length > 0 && (
        <div className="fixed bottom-4 lg:bottom-6 left-1/2 -translate-x-1/2 z-50 liquid-glass rounded-2xl px-4 py-3 flex items-center gap-3 shadow-xl" data-testid="bulk-action-bar">
          <span className="text-sm text-slate-200"><span className="font-mono text-teal-300">{pickedIds.length}</span> selected</span>
          <Button onClick={bulkSave} data-testid="bulk-save-btn" size="sm" className="bg-gradient-to-r from-teal-400 to-cyan-500 text-slate-900 font-semibold"><Bookmark size={14} className="mr-1.5" /> Save all</Button>
          {agency && <Button onClick={() => setBulkOpen(true)} data-testid="bulk-submit-btn" size="sm" className="bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 font-semibold"><Send size={14} className="mr-1.5" /> Submit to client</Button>}
          <button onClick={clearSelection} data-testid="bulk-clear-btn" className="text-slate-400 hover:text-white"><X size={18} /></button>
        </div>
      )}

      {/* save-search dialog */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="liquid-glass border-white/10 text-slate-100">
          <DialogHeader><DialogTitle>Save this search</DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">You'll get an alert when a new candidate matching these filters joins.</DialogDescription>
          </DialogHeader>
          <Input value={searchName} onChange={(e) => setSearchName(e.target.value)} data-testid="save-search-name" placeholder="e.g. Senior React engineers" className="bg-slate-950/50 border-white/10 text-slate-100" />
          <DialogFooter>
            <Button onClick={saveSearch} data-testid="save-search-confirm" className="bg-gradient-to-r from-teal-400 to-cyan-500 text-slate-900 font-semibold">Save search</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* bulk submit dialog (agency) */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="liquid-glass border-white/10 text-slate-100">
          <DialogHeader><DialogTitle>Submit {pickedIds.length} candidates to a client</DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">Present the selected candidates to one of your client companies.</DialogDescription>
          </DialogHeader>
          <Select value={bulkClient} onValueChange={setBulkClient}>
            <SelectTrigger data-testid="bulk-client-select" className="bg-slate-950/50 border-white/10 text-slate-200"><SelectValue placeholder="Select client company…" /></SelectTrigger>
            <SelectContent className="text-slate-200 max-h-64">
              {clients.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">Add a client company first</div>}
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Textarea value={bulkNote} onChange={(e) => setBulkNote(e.target.value)} data-testid="bulk-note" placeholder="Note to the client (optional)" className="bg-slate-950/50 border-white/10 text-slate-100 text-sm" />
          <DialogFooter>
            <Button onClick={bulkSubmit} data-testid="bulk-submit-confirm" className="bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 font-semibold"><Send size={15} className="mr-2" /> Submit candidates</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {selected && (
        <CandidateDrawer candidate={selected} isAgency={agency} jobs={jobs} clients={clients}
          onClose={() => setSelected(null)} onToggleSave={toggleSave} />
      )}
    </div>
  );
}
